import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import type { AiUsageBreakdownItem } from '@respectly/shared-types';
import { COST_ACCENT, formatCompact, formatUsd } from './format';

/** A ranked "cost by X" list with inline share bars — the workhorse breakdown. */
export function BreakdownList({
  title,
  items,
  emptyHint,
}: {
  title: string;
  items: AiUsageBreakdownItem[];
  emptyHint?: string;
}) {
  const max = items.reduce((m, i) => Math.max(m, i.costUsd), 0) || 1;
  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">{emptyHint ?? 'No data in this range yet.'}</p>
        ) : (
          <ul className="space-y-3">
            {items.slice(0, 8).map((it) => (
              <li key={it.key}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-medium text-slate-700 dark:text-slate-200" title={it.label}>
                    {it.label}
                  </span>
                  <span className="shrink-0 font-mono tabular-nums text-slate-900 dark:text-slate-50">
                    {formatUsd(it.costUsd, { precise: true })}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(it.costUsd / max) * 100}%`, background: COST_ACCENT }}
                    />
                  </div>
                  <span className="w-28 shrink-0 text-right text-xs tabular-nums text-slate-400">
                    {formatCompact(it.totalTokens)} tok · {it.costShare}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
